import {
  Label,
  PaginatedCombobox,
  useApiClient,
  type PaginatedResponse,
  type PaginationParams,
} from '@startupquickstart/frontend';

interface AmenityType {
  id: string;
  name: string;
  description?: string;
}

interface AmenityTypeComboboxProps {
  value?: string;
  onValueChange: (value: string, amenityType?: AmenityType) => void;
  label?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  pageSize?: number;
  required?: boolean;
  error?: string;
  initialItem?: AmenityType;
}

/**
 * Paginated combobox for selecting amenity types
 * Reusable component for forms and filters
 */
export function AmenityTypeCombobox({
  value = '',
  onValueChange,
  label = 'Amenity Type',
  placeholder = 'Select amenity type...',
  searchPlaceholder = 'Search amenity types...',
  emptyMessage = 'No amenity types found.',
  pageSize = 20,
  required = false,
  error,
  initialItem,
}: AmenityTypeComboboxProps) {
  const apiClient = useApiClient();

  /**
   * Fetch amenity types with pagination and search
   */
  const getAmenityTypes = async (
    params: PaginationParams,
  ): Promise<PaginatedResponse<AmenityType>> => {
    const response = await apiClient.get<PaginatedResponse<AmenityType>>('/amenity-types', {
      params: {
        page: params.page,
        limit: params.limit,
        orderBy: { name: 'asc' },
        ...(params.search && { search: params.search }),
      },
    });
    return response;
  };

  return (
    <div className="grid gap-2">
      {label && (
        <Label>
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </Label>
      )}
      <PaginatedCombobox<AmenityType>
        getData={getAmenityTypes}
        getValueFromItem={item => item.id}
        getLabelFromItem={item => item.name}
        value={value}
        onValueChange={onValueChange}
        placeholder={placeholder}
        searchPlaceholder={searchPlaceholder}
        emptyMessage={emptyMessage}
        pageSize={pageSize}
        initialItem={initialItem}
      />
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}
